import React, { useMemo, useState, useEffect } from 'react';
import { Box, Paper, Typography, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import { colors, avatars, translations } from '.';

const HOURS = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
const HOUR_HEIGHT = 40;
const forwardType = {
  'schedule': 'editSchedule',
  'proposal': 'editProposal',
  'serviceorder': 'editServiceOrder',
};

const getDuration = (item) => {
  if (item.end_datetime && item.datetime) {
    const start = dayjs(item.datetime);
    const end = dayjs(item.end_datetime);

    if (start.isValid() && end.isValid()) {
      const diff = end.diff(start, 'minute');
      if (diff > 0 && diff < 1440) {
        return diff;
      }
    }
  }


  const durationFields = [
    'duration',
    'duracao_minutos',
    'minutes',
    'duracao',
    'tempo_estimado'
  ];

  for (const field of durationFields) {
    if (item[field] !== undefined && item[field] !== null) {
      const duration = parseInt(item[field], 10);

      if (duration > 0 && duration < 1439) {
        return duration;
      }
      else {
        return dayjs(item.datetime).endOf('day').diff(dayjs(item.datetime), 'minute');
      }
    }
  }

  const defaultDurations = {
    'schedule': 60,
    'proposal': 30,
    'serviceorder': 120
  };

  return defaultDurations[item.type] || 60;
};

const DayCell = ({ data, initialDate, openItem, reloadData, parent, filter, parentSet }) => {
  const [currentDate, setCurrentDate] = useState(() => {
    return initialDate || dayjs();
  });

  const [activeFilters, setActiveFilters] = useState(() => {
    const filters = [];
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value.active) filters.push(key);
      });
    }
    return filters;
  });

  useEffect(() => {
    if (initialDate && !initialDate.isSame(currentDate, 'day')) {
      setCurrentDate(initialDate);
    }
  }, [initialDate?.format('YYYY-MM-DD')]);

  useEffect(() => {
    if (filter) {
      const filters = [];
      Object.entries(filter).forEach(([key, value]) => {
        if (value.active) filters.push(key);
      });
      setActiveFilters(filters);
    }
  }, [filter]);

  const goToNextDay = () => {
    const nextDay = currentDate.add(1, 'day');
    setCurrentDate(nextDay);

    if (openItem) {
      openItem({ selectedDay: nextDay });
    }
  };

  const goToPrevDay = () => {
    const prevDay = currentDate.subtract(1, 'day');
    setCurrentDate(prevDay);

    if (openItem) {
      openItem({ selectedDay: prevDay });
    }
  };

  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data.filter(item => {
      if (!item.datetime || !dayjs(item.datetime).isValid()) {
        console.warn('Item com datetime inválido:', item);
        return false;
      }

      const matchesDate = dayjs(item.datetime).format('YYYY-MM-DD') === currentDate.format('YYYY-MM-DD');

      const matchesTypeAndState = activeFilters.includes(item.type) && activeFilters.includes(item.state);

      return matchesDate && matchesTypeAndState;
    });
  }, [data, currentDate, activeFilters]);

  const teams = useMemo(() => {
    const teamSet = new Set();
    filteredData.forEach(item => {
      if (item.team_name) teamSet.add(item.team_name);
    });
    return Array.from(teamSet);
  }, [filteredData]);

  const getServiceDimensions = (start, end, item) => {
    if (!start || !start.isValid || !start.isValid()) {
      console.error('Data de início inválida:', start);
      return { top: 0, height: HOUR_HEIGHT };
    }

    const startMin = start.hour() * 60 + start.minute();
    const top = Math.round((startMin / 60) * HOUR_HEIGHT);

    const duracao = getDuration(item);

    const minHeight = 20;

    const heightFromDuration = (duracao / 60) * HOUR_HEIGHT;

    const height = Math.max(heightFromDuration, minHeight);

    return { top, height };
  };

  const getEndTime = (item, startTime) => {
    if (!startTime || !startTime.isValid()) {
      console.error('startTime inválido:', startTime);
      return dayjs().add(1, 'hour');
    }

    if (item.end_datetime) {
      const endTime = dayjs(item.end_datetime);
      if (endTime.isValid() && endTime.isAfter(startTime)) {
        return endTime;
      }
    }

    const duration = getDuration(item);

    return startTime.add(duration, 'minute');
  };

  const calculateServiceLayout = (items) => {
    const positioned = [];

    const sortedItems = [...items].sort((a, b) =>
      dayjs(a.datetime).diff(dayjs(b.datetime))
    );

    const OVERLAP_MARGIN_MINUTES = 15;

    sortedItems.forEach((item) => {
      const start = dayjs(item.datetime);

      if (!start.isValid()) {
        console.warn('Item com datetime inválido:', item);
        return;
      }

      const end = getEndTime(item, start);

      const overlapping = positioned.filter((pos) => {
        const posStart = dayjs(pos.item.datetime);
        const posEnd = getEndTime(pos.item, posStart);

        const realOverlap = !(end.isBefore(posStart) || start.isAfter(posEnd));


        const startDiff = Math.abs(start.diff(posEnd, 'minute'));
        const endDiff = Math.abs(end.diff(posStart, 'minute'));
        const nearbyEvents = startDiff <= OVERLAP_MARGIN_MINUTES || endDiff <= OVERLAP_MARGIN_MINUTES;


        return realOverlap || nearbyEvents;
      });

      let slot = 0;
      let slotsOccupied = new Set(overlapping.map(o => o.slot));

      while (slotsOccupied.has(slot)) {
        slot++;
      }

      positioned.push({
        item,
        slot,
        start,
        end,
        overlappingWith: overlapping
      });
    });

    const calculateTotalSlots = (pos) => {
      let allOverlapping = new Set();
      let toProcess = [pos];
      let processed = new Set();

      while (toProcess.length > 0) {
        const current = toProcess.pop();
        if (processed.has(current)) continue;

        processed.add(current);


        positioned.forEach(other => {
          if (other !== current && !processed.has(other)) {
            const otherStart = other.start;
            const otherEnd = other.end;

            const realOverlap = !(current.end.isBefore(otherStart) || current.start.isAfter(otherEnd));

            const startDiff = Math.abs(current.start.diff(otherEnd, 'minute'));
            const endDiff = Math.abs(current.end.diff(otherStart, 'minute'));
            const nearbyEvents = startDiff <= OVERLAP_MARGIN_MINUTES || endDiff <= OVERLAP_MARGIN_MINUTES;

            if (realOverlap || nearbyEvents) {
              allOverlapping.add(other);
              toProcess.push(other);
            }
          }
        });
      }

      const maxSlot = Math.max(
        pos.slot,
        ...Array.from(allOverlapping).map(o => o.slot)
      );

      return maxSlot + 1;
    };

    positioned.forEach(pos => {
      pos.totalSlots = calculateTotalSlots(pos);
    });

    return positioned;
  };

  const unassignedItems = useMemo(() => {
    return filteredData.filter(item => !item.team_name);
  }, [filteredData]);

  const displayTeams = useMemo(() => {
    if (unassignedItems.length > 0) {
      if (teams.length === 0) {
        return ["Sem equipe"];
      }
      return [...teams, "Sem equipe"];
    }
    return teams;
  }, [teams, unassignedItems]);

  const totalHeight = HOURS.length * HOUR_HEIGHT;

  const formatDuration = (minutes) => {
    if (minutes < 1) return "< 1min";

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) {
      return `${mins}min`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h${mins}min`;
    }
  };

  return (
    <Box id="day-schedule-print" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', backgroundColor: '#f1f1f1' }}>
        <IconButton onClick={goToPrevDay} >
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6">
          {currentDate.format('dddd, DD [de] MMMM [de] YYYY')}
          {filteredData.length === 0 && ' (Nenhum agendamento)'}
        </Typography>
        <IconButton onClick={goToNextDay}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 48px)', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `100px repeat(${displayTeams.length || 1}, 1fr)`,
              borderBottom: '1px solid #ccc',
              backgroundColor: '#f9f9f9',
              position: 'sticky',
              top: 0,
              zIndex: 3,
            }}
          >

            <Box sx={{ padding: 1, textAlign: 'center', fontWeight: 'bold' }}>
              Horário
            </Box>


            {displayTeams.length > 0 ? displayTeams.map(team => (
              <Box
                key={`team-header-${team}`}
                sx={{
                  padding: 1,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  borderLeft: '1px solid #ddd',
                  fontStyle: team === "Sem equipe" ? 'italic' : 'normal',
                }}
              >
                {team}
              </Box>
            )) : (
              <Box
                sx={{
                  padding: 1,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  borderLeft: '1px solid #ddd',
                  fontStyle: 'italic',
                  color: '#888'
                }}
              >
                Sem equipes agendadas
              </Box>
            )}
          </Box>

          <Box className="schedule-grid-container"
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              height: '100%',
              pb: 2
            }}
          >
            <Box
              className="schedule-grid"
              sx={{
                display: 'grid',
                gridTemplateColumns: `100px repeat(${displayTeams.length || 1}, 1fr)`,
                width: '100%',
                position: 'relative',
                height: `${totalHeight + 20}px`
              }}
            >

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: `${totalHeight}px`
              }}>
                {HOURS.map((hour, index) => (
                  <Box
                    key={hour}
                    className="hour-cell"
                    sx={{
                      borderBottom: '1px solid #ddd',
                      padding: '0.5rem',
                      backgroundColor: '#f9f9f9',
                      position: 'sticky',
                      left: 0,
                      height: `${HOUR_HEIGHT}px`,
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.85rem'
                    }}
                  >
                    {hour}
                  </Box>
                ))}
              </Box>


              {displayTeams.map(team => {
                const teamItems = team === "Sem equipe"
                  ? filteredData.filter(item => !item.team_name)
                  : filteredData.filter(item => item.team_name === team);

                const positionedItems = calculateServiceLayout(teamItems);

                return (
                  <Box
                    key={team}
                    className="team-column"
                    sx={{
                      borderLeft: '1px solid #eee',
                      position: 'relative',
                      height: `${totalHeight}px`,
                    }}
                  >
                    {HOURS.map((hour, index) => (
                      <Box
                        key={`${team}-hour-${index}`}
                        sx={{
                          position: 'absolute',
                          top: `${index * HOUR_HEIGHT}px`,
                          left: 0,
                          right: 0,
                          height: `${HOUR_HEIGHT}px`,
                          borderBottom: '1px solid #ddd',
                          zIndex: 0,
                        }}
                      />
                    ))}

                    {positionedItems.map(({ item, slot, totalSlots, start, end }, i) => {
                      const { top, height } = getServiceDimensions(start, end, item);
                      const duration = getDuration(item);

                      const slotWidth = totalSlots === 1 ? 100 : 100 / totalSlots;
                      const width = `calc(${slotWidth}% - 6px)`;
                      const left = `calc(${slotWidth * slot}% + 3px)`;

                      const isCompact = height < 50;

                      const hasRealEndTime = item.end_datetime && dayjs(item.end_datetime).isValid();
                      return (
                        <Paper
                          key={`${item.type}-${item.id}-${i}`}
                          sx={{
                            position: 'absolute',
                            top: `${top}px`,
                            left: left,
                            width: width,
                            height: `${height}px`,
                            backgroundColor: colors[item.state] || '#0d6efd',
                            padding: isCompact ? '2px 4px' : '4px 8px',
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            borderRadius: '4px',
                            color: 'white',
                            zIndex: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            display: 'flex',
                            flexDirection: isCompact ? 'row' : 'column',
                            alignItems: isCompact ? 'center' : 'flex-start',
                            gap: isCompact ? '4px' : '2px',
                            border: hasRealEndTime ? 'none' : '2px dashed rgba(255, 255, 255, 0.5)',
                            '&:hover': {
                              boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
                              zIndex: 10,
                              transform: 'scale(1.02)',
                              transition: 'all 0.2s ease',
                              overflow: 'visible'
                            }
                          }}
                          onClick={() => openItem({ [forwardType[item.type]]: item })}
                        >
                          {isCompact ? (
                            <>
                              <Box sx={{
                                display: 'flex',
                                flexShrink: 0,
                                '& svg': { width: '14px', height: '14px' }
                              }}>
                                {avatars[item.type]}
                              </Box>
                              <Typography variant="caption" sx={{
                                fontSize: '0.65rem',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                flex: 1
                              }}>
                                {start.format('HH:mm')} {!hasRealEndTime && `(~${formatDuration(duration)})`} | {item.client_name}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                flexWrap: 'wrap',
                                lineHeight: '1.2'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {avatars[item.type]}
                                </Box>
                                <Typography variant="caption" sx={{
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {start.format('HH:mm')} - {hasRealEndTime ? end.format('HH:mm') : `~${end.format('HH:mm')}`}
                                </Typography>
                                {!hasRealEndTime && (
                                  <Typography variant="caption" sx={{
                                    fontSize: '0.6rem',
                                    opacity: 0.8,
                                    fontStyle: 'italic'
                                  }}>
                                    (estimado)
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="caption" sx={{
                                fontWeight: 'bold',
                                lineHeight: '1.2',
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}>
                                {item.client_name}
                              </Typography>
                              {item.service_names && height > 70 && (
                                <Typography variant="caption" sx={{
                                  fontSize: '0.65rem',
                                  lineHeight: '1.1',
                                  opacity: 0.95,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: 'vertical'
                                }}>
                                  {item.service_names}
                                </Typography>
                              )}
                              {height > 90 && (
                                <Typography variant="caption" sx={{
                                  fontSize: '0.6rem',
                                  lineHeight: '1.1',
                                  opacity: 0.85,
                                  fontStyle: 'italic',
                                  marginTop: 'auto'
                                }}>
                                  Duração: {formatDuration(duration)}
                                </Typography>
                              )}
                            </>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                );
              })}

              {displayTeams.length === 0 && (
                <Box
                  sx={{
                    borderLeft: '1px solid #eee',
                    position: 'relative',
                    height: `${totalHeight}px`,
                  }}
                >
                  {HOURS.map((hour, index) => (
                    <Box
                      key={`empty-hour-${index}`}
                      sx={{
                        position: 'absolute',
                        top: `${index * HOUR_HEIGHT}px`,
                        left: 0,
                        right: 0,
                        height: `${HOUR_HEIGHT}px`,
                        borderBottom: '1px solid #ddd',
                        zIndex: 0,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DayCell;